import { ACTIVE, INVITED, userStatus } from '../../Settings/user.const';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import { EVERYONE_GROUP_ID, USER_MATCHING_ITEM_PREFIX } from '@/components/dialogs/ShareModuleDialog/share-module-dialog.const.js';

const SYNCED_GROUPS_HEADER = { header: 'Synced Groups', itemId: 'synced_group' };
const OTHER_GROUPS_AND_USERS_HEADER = { header: 'Other Groups & Users', itemId: 'other' };

const USERS_HEADER = { header: 'Users', itemId: 'user' };
const GROUPS_HEADER = { header: 'Groups', itemId: 'group' };

const FAKE_EVERYONE_GROUP = {
	isGroup: true,
	sub_groups: [],
	isEveryone: true,
	id: EVERYONE_GROUP_ID,
	name: 'Everyone in workspace',
	internalIcon: 'everyone-group',
	itemId: 'group_everyone_in_workspace',
};
const getFakeEveryoneGroup = (teamMembers) => ({
	...FAKE_EVERYONE_GROUP,
	users: teamMembers.map(({ user_id }) => user_id),
	all_users: teamMembers.map(({ user_id }) => user_id),
});

/* Private */
function integrationId(group) {
	return group?.synced?.service;
}

function isSharedGroup(sharedGroups, groupId) {
	return sharedGroups.includes(Number(groupId));
}

function isSyncedGroup(relatedSyncedGroups, groupId) {
	return relatedSyncedGroups.includes(Number(groupId));
}

function isSharedUser(sharedUsers, userId) {
	return sharedUsers.includes(userId);
}

function alreadyIn(list, userEmail) {
	return list.includes(userEmail);
}

function isActiveOrInvited(user) {
	return user.status === userStatus[ACTIVE] || user.status === userStatus[INVITED];
}

/* Public */
async function openDialog(
	workflow,
	canShare,
	openSuggestionsToMoveCredentialsToUserConfiguration,
	flowType,
	serviceSlug,
	openDialogCallback
) {
	const workflowId = workflow.id;
	const shouldCompleteSetupBeforeShare = Boolean(workflow.installed_from && !workflow.is_general_setup_completed);
	if (shouldCompleteSetupBeforeShare) {
		const res = await AppDialogManagerService.openConfirmationDialog({
			title: 'Complete setup',
			description: 'General setup must be completed before sharing',
			yesText: 'OK',
			isDanger: false,
		});
		return res === 'yes' ? 'should_open_general_setup' : null;
	} else if (canShare) {
		return openSuggestionsToMoveCredentialsToUserConfiguration({
			workflow,
			isFromPublish: false,
			next: () => openDialogCallback({ workflowId, flowType, serviceSlug }),
		});
	}
}

function getGroupedTeamMembersToShareWith(
	groups,
	teamMembers,
	otherTeamMembers,
	usersAlreadySharedWith,
	groupsAlreadySharedWith,
	relatedSyncedGroups,
	sharedWorkflowData,
	includeEveryone,
	canInviteToWorkspace
) {
	let mappedRelatedSyncedGroups = [];
	let mappedRelatedUserMatching = [];
	let groupsToShare = [];
	let result = [];
	const usersAlreadySharedWithIds = usersAlreadySharedWith.map((x) => x.user_id);
	const usersAlreadySharedWithEmails = usersAlreadySharedWith.map((x) => x.email);
	const groupsAlreadySharedWithIds = groupsAlreadySharedWith.map((x) => x.id);
	const memberEmails = teamMembers.map((x) => x.email);
	const relatedSyncedGroupsIds = sharedWorkflowData.related_synced_groups || [];
	const related_user_matching = sharedWorkflowData.related_user_matching || [];

	const related_service_name = sharedWorkflowData.related_service_name || 'Suggested';
	const USERS_MATCHED_HEADER = { header: `${related_service_name} Users`, itemId: USER_MATCHING_ITEM_PREFIX };

	mappedRelatedSyncedGroups = relatedSyncedGroups
		.filter((group) => !isSharedGroup(groupsAlreadySharedWithIds, group.id))
		.map((group) => {
			group.itemId = `synced_group_${group.name}_${group.id}`;
			group.isGroup = true;
			return group;
		});

	mappedRelatedUserMatching = related_user_matching
		.filter((user) => !isSharedUser(usersAlreadySharedWithEmails, user.email) && !alreadyIn(memberEmails, user.email))
		.map((user) => {
			user.itemId = `${USER_MATCHING_ITEM_PREFIX}_${user.email}_${user.id}`;
			user.isUser = true;
			user.icon = 'email';
			user.suggestion = true;
			return user;
		});

	if (groups) {
		groupsToShare = groups
			.filter((group) => !isSharedGroup(groupsAlreadySharedWithIds, group.id) && !isSyncedGroup(relatedSyncedGroupsIds, group.id))
			.map((group) => {
				group.itemId = `group_${group.name}_${integrationId(group)}_${group.id}`;
				group.isGroup = true;

				return group;
			});
	}

	const usersToShare = otherTeamMembers
		.filter((user) => isActiveOrInvited(user) && !isSharedUser(usersAlreadySharedWithIds, user.user_id))
		.map((user) => {
			const name = user.name || user.email;
			user.itemId = `user_${name}_${user.id}`;
			user.isUser = true;
			return user;
		});

	if (mappedRelatedSyncedGroups.length > 0) {
		result = [...result, SYNCED_GROUPS_HEADER, ...mappedRelatedSyncedGroups];
	}
	if (mappedRelatedUserMatching.length > 0) {
		if (canInviteToWorkspace) {
			result = [...result, USERS_MATCHED_HEADER, ...mappedRelatedUserMatching];
		} else {
			const USERS_MATCHED_NO_PERMISSION_HEADER = {
				header: `${mappedRelatedUserMatching.length} ${related_service_name} Users found, but you do not have permission to invite users to this workspace. Request access from an admin`,
				itemId: USER_MATCHING_ITEM_PREFIX,
			};
			result = [...result, USERS_MATCHED_HEADER, USERS_MATCHED_NO_PERMISSION_HEADER, { header: ' ', itemId: 'spacer' }];
		}
	}
	let headers = [USERS_HEADER];
	if (mappedRelatedSyncedGroups.length > 0 || mappedRelatedUserMatching.length > 0) {
		headers = [OTHER_GROUPS_AND_USERS_HEADER, USERS_HEADER];
	}
	if (usersToShare.length > 0) {
		result = [...result, ...headers, ...usersToShare];
	}
	if (groupsToShare.length > 0) {
		result = [...result, GROUPS_HEADER, ...groupsToShare];
	}
	if (includeEveryone) {
		result = [getFakeEveryoneGroup(teamMembers), ...result];
	}
	return result;
}

const ShareModuleDialogService = {
	openDialog,
	getGroupedTeamMembersToShareWith,
	getFakeEveryoneGroup,
};

export default ShareModuleDialogService;
